/**
 * Bazaarvoice inline rating (stars + review count) for PDP and product tiles.
 * @see https://docs.bazaarvoice.com/articles/#!ratings-reviews/display_integration
 *
 * - PDP (`default` / `strip`): `rating_summary` — histogram / reviews CTA allowed
 * - PLP tile: `inline_rating` — display only (Figma PLP tile; KINDW-254). Sit inside the
 *   tile `<Link>`; `pointer-events: none` + `aria-hidden` so clicks/focus stay on the link.
 *   Parent should surface the count in the link `aria-label` via `onReviewCountChange`.
 */
import PropTypes from 'prop-types';
import {useEffect, useRef} from 'react';

import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {useBazaarvoiceScript} from '@amblique/king-living/app/hooks/use-bazaarvoice-script';
import {
  getBazaarvoiceProductId,
  isBazaarvoiceConfigured,
} from '@amblique/king-living/app/utils/bazaarvoice';
import {scrollElementBelowStickyHeader} from '@amblique/king-living/app/utils/utils';

const TILE_REVIEW_COUNT_CLASS = 'sf-bv-tile-review-count';
const SYNC_DEBOUNCE_MS = 64;

/** BV "same page link" triggers in `rating_summary` (stars + review count). Excludes write-a-review CTAs. */
const BV_SAME_PAGE_LINK_SELECTOR = 'a[href^="#"], .bv_main_rating_button';

/**
 * Reviews anchor sits far up the page; the header reappears on the upward scroll, so re-snap
 * against live chrome once the smooth scroll settles. Keep the wave short — long delayed
 * resnaps fight tiny user scrolls after landing (DE-326 jank).
 */
const REVIEWS_SCROLL_OPTIONS = {
  syncLiveChromeAfterSmoothScroll: {delaysMs: [48, 160, 320]},
};

/**
 * Parse review count from BV DOM and format for Figma.
 * PDP: "(12 Reviews)" · PLP tile: "(12 reviews)"
 *
 * `inline_rating` has no `.bv_numReviews_text` — only off-screen copy + avg score.
 * Tile CSS hides the score; we inject a visible "(N reviews)" span.
 *
 * @param {HTMLElement} root
 * @returns {number|null} Parsed review count, or null if unavailable
 */
function syncBazaarvoiceReviewCount(root) {
  const isTile = root.dataset.bvVariant === 'tile';
  let reviewsText = root.querySelector('.bv_numReviews_text');
  const offscreen =
    root.querySelector('.bv-off-screen, .bv_offscreen_text')?.textContent?.trim() || '';

  const match =
    reviewsText?.textContent?.match(/\(?([\d,]+)\)?/) ||
    offscreen.match(/([\d,]+)\s+reviews?\b/i);

  if (!match) {
    return null;
  }

  const count = match[1].replace(/,/g, '');
  const countNumber = Number.parseInt(count, 10);
  const formatted = isTile ? `(${count} reviews)` : `(${count} Reviews)`;

  if (isTile && !reviewsText) {
    const main = root.querySelector('.bv_main_container');

    if (!main) {
      return Number.isFinite(countNumber) ? countNumber : null;
    }

    reviewsText = document.createElement('span');
    reviewsText.className = `bv_numReviews_text ${TILE_REVIEW_COUNT_CLASS}`;
    reviewsText.setAttribute('aria-hidden', 'true');
    main.appendChild(reviewsText);
  }

  if (!reviewsText) {
    return Number.isFinite(countNumber) ? countNumber : null;
  }

  // PDP: leave BV's own "Reviews" copy once it has rendered.
  if (!isTile && /reviews/i.test(reviewsText.textContent || '')) {
    return Number.isFinite(countNumber) ? countNumber : null;
  }

  if (reviewsText.textContent !== formatted) {
    reviewsText.textContent = formatted;
  }

  return Number.isFinite(countNumber) ? countNumber : null;
}

/**
 * Defensive: BV may inject focusable controls; keep tile rating non-interactive
 * when nested inside the product `<Link>`.
 *
 * @param {HTMLElement} root
 */
function disarmTileInteractiveChildren(root) {
  if (root.dataset.bvVariant !== 'tile') {
    return;
  }

  root.querySelectorAll('button, a, [tabindex]').forEach((el) => {
    if (el.getAttribute('tabindex') === '-1' && el.getAttribute('aria-hidden') === 'true') {
      return;
    }

    el.setAttribute('tabindex', '-1');
    el.setAttribute('aria-hidden', 'true');
  });
}

/**
 * @param {object} props
 * @param {object} props.product - SCAPI product or search hit
 * @param {string} [props.redirectUrl] - PDP reviews hash (default / strip only)
 * @param {'default'|'tile'|'strip'} [props.variant]
 * @param {(count: number) => void} [props.onReviewCountChange] - Tile: notify parent for link aria-label
 * @returns {React.ReactElement|null}
 */
export function BazaarvoiceInlineRating({
  product,
  redirectUrl,
  variant = 'default',
  onReviewCountChange,
}) {
  const containerRef = useRef(null);
  const lastCountRef = useRef(null);
  const bazaarvoicePrefs = useSitePreference('bazaarvoice');
  const isConfigured = isBazaarvoiceConfigured(bazaarvoicePrefs);
  const productId = getBazaarvoiceProductId(product);
  const styles = useMultiStyleConfig('BazaarvoiceInlineRating', {variant});
  const isBvReady = useBazaarvoiceScript(isConfigured);
  const isTile = variant === 'tile';

  useEffect(() => {
    const root = containerRef.current;

    if (!isBvReady || !root) {
      return undefined;
    }

    lastCountRef.current = null;
    if (isTile) {
      delete root.dataset.bvEmpty;
    }

    let debounceId;

    const sync = () => {
      window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        const count = syncBazaarvoiceReviewCount(root);
        disarmTileInteractiveChildren(root);

        // PLP tile: collapse empty BV shells (no reviews / unknown product id in feed)
        // so we don't leave a blank gap under the price.
        if (isTile) {
          const bvShow = root.querySelector('[data-bv-show]');
          const ready = bvShow?.getAttribute('data-bv-ready') === 'true';

          if (ready) {
            const hasStars =
              (bvShow?.querySelectorAll('.bv_stars_component_container svg')?.length ?? 0) > 0;
            const hiddenByBv = Boolean(bvShow?.querySelector('.bv_hide_visibility'));
            // Show when we have a real count or rendered stars; otherwise collapse.
            const empty = hiddenByBv || ((count == null || count === 0) && !hasStars);

            root.dataset.bvEmpty = empty ? 'true' : 'false';
          }
        }

        if (
          typeof onReviewCountChange === 'function' &&
          count != null &&
          count !== lastCountRef.current
        ) {
          lastCountRef.current = count;
          onReviewCountChange(count);
        }
      }, SYNC_DEBOUNCE_MS);
    };

    sync();

    const observer = new MutationObserver(sync);

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-bv-ready'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => {
      window.clearTimeout(debounceId);
      observer.disconnect();
    };
  }, [isBvReady, isTile, productId, onReviewCountChange]);

  // BV resolves `data-bv-redirect-url` itself and jumps straight to the anchor, which leaves the
  // reviews section behind the sticky header (most visible from the add-to-cart strip, where the
  // jump is upwards and the header reappears). Capture the click before it reaches BV's own
  // handler and scroll with the shared header-aware helper instead.
  useEffect(() => {
    const root = containerRef.current;
    const isHashTarget = typeof redirectUrl === 'string' && redirectUrl.startsWith('#');

    if (!isBvReady || !root || isTile || !isHashTarget) {
      return undefined;
    }

    const handleClickCapture = (event) => {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey) {
        return;
      }

      const trigger = event.target?.closest?.(BV_SAME_PAGE_LINK_SELECTOR);

      // No anchor on the page (e.g. CMS module not placed) — leave BV's default behaviour alone.
      if (!trigger || !document.getElementById(redirectUrl.slice(1))) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      scrollElementBelowStickyHeader(redirectUrl, undefined, REVIEWS_SCROLL_OPTIONS);
    };

    root.addEventListener('click', handleClickCapture, true);

    return () => root.removeEventListener('click', handleClickCapture, true);
  }, [isBvReady, isTile, redirectUrl]);

  if (!isConfigured || !productId) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      aria-hidden={isTile ? true : undefined}
      className="sf-bazaarvoice-inline-rating"
      data-bv-variant={variant}
      data-testid="bazaarvoice-inline-rating"
      sx={styles.container}
    >
      <div
        data-bv-product-id={productId}
        data-bv-redirect-url={isTile ? undefined : redirectUrl || undefined}
        data-bv-seo="false"
        data-bv-show={isTile ? 'inline_rating' : 'rating_summary'}
      />
    </Box>
  );
}

BazaarvoiceInlineRating.propTypes = {
  product: PropTypes.object,
  redirectUrl: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'tile', 'strip']),
  onReviewCountChange: PropTypes.func,
};

export default BazaarvoiceInlineRating;
